import { Spinner } from '@forest/shared/solid'
import { Show, type JSXElement } from 'solid-js'
import type { RemoteArtworkStatus } from '~/lib/remote-image'

// A fixed-size image area for art that arrives as a remote URL (ADR 0013):
// a spinner while the browser loads it, the image swapped into the same area
// on load, and the area left blank on failure — never a layout shift in any
// state. The caller owns the status so the same load can be shared across
// surfaces (currency art) or kept local (the land-skin detail).
export function RemoteArtwork(props: {
  children?: JSXElement
  class: string
  onError: () => void
  onLoad: () => void
  src: string
  status: RemoteArtworkStatus
  testId: string
}) {
  return (
    <div
      data-testid={props.testId}
      data-artwork-status={props.status}
      class={`relative flex shrink-0 items-center justify-center ${props.class}`}
    >
      <Show when={props.status === 'loading'}>
        {/* Decorative art's loading state: hidden from assistive tech, so a
            grid of cards doesn't announce a status per image. */}
        <div aria-hidden="true" data-testid="remote-artwork-spinner">
          <Spinner class="size-5 text-ink/60" />
        </div>
      </Show>
      <Show when={props.status !== 'failed'}>
        <img
          src={props.src}
          alt=""
          // Decorative art is never dragged: the land-skin detail's swipe
          // starts on it, and a native image drag would take the gesture.
          draggable={false}
          onLoad={() => props.onLoad()}
          onError={() => props.onError()}
          class="absolute inset-0 size-full object-contain transition-opacity"
          classList={{
            'opacity-0': props.status === 'loading',
            'opacity-100': props.status === 'loaded',
          }}
        />
      </Show>
      {props.children}
    </div>
  )
}
